import React, { useEffect, useState } from "react";
import ViewTable from "../../../Components/Common/ViewTable";
import { get, post, put, del, api } from "../../../utils/api";
import { formatDate } from "../../../utils/formatDate";
import Cards from "../../../Components/Common/Cards";
import FilterModal from "../../../Components/Common/FilterModal";
import ActionButtons from "../../../Components/Common/ActionButtons";
import ViewData from "./ViewData";
import Form from "./Form";
import { Alert, Snackbar } from "@mui/material";
import BulkUploadModal from "../../../Components/Common/BulkUploadModal";
import DeleteAlertDialog from "../../../Components/Masters/DeleteAlertDialog";
import BulkUploadReview from "./BulkUploadReview";

const Franchises = () => {
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });
  const [tableData, setTableData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [filterObj, setFilterObj] = useState({});
  const [deletedItemId, setDeletedItemId] = useState("");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [bulkUploadModal, setBulkUploadModal] = useState(false);
  const [excelData, setExcelData] = useState(null);
  const [bulkUploadTable, setBulkUploadTable] = useState(false);
  const [formDataBulkUpload, setFormDataBulkUpload] = useState([]);
  const [matchData, setMatchData] = useState([]);
  const [masterFranchises, setMasterFranchises] = useState([]);
  const [franchiseSearchTerm, setFranchiseSearchTerm] = useState("");

  const [filters, setFilters] = useState({
    country: [],
    franchiseType: [],
  });

  const [cardData, setCardData] = useState({
    totalEntries: 0,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [snackOpen, setsnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const headers = [
    {key:"createdAt", label: "Date", sortable: true},
    { key: "franchiseName", label: "Franchise Name", sortable: true },
    { key: "country", label: "Country", sortable: true },
    { key: "franchiseType", label: "Franchise Type", sortable: true },
    { key: "reportingPeriod", label: "Reporting Period", sortable: false },
  ];

  const inputData = [
    { fieldName: "franchiseId", title: "Franchise ID" },
    { fieldName: "franchiseName", title: "Franchise Name" },
    { fieldName: "country", title: "Country" },
    { fieldName: "locationAddress", title: "Location/Address" },
    { fieldName: "reportingStart", title: "Reporting Period Start Date" },
    { fieldName: "reportingEnd", title: "Reporting Period End Date" },
    { fieldName: "contactPerson", title: "Contact Person" },
    { fieldName: "franchiseType", title: "Franchise Type" },
    {
      fieldName: "scope1EmissionsMetricTon",
      title: "Scope 1 Emissions (Metric Ton)",
    },
    {
      fieldName: "scope2EmissionsMetricTon",
      title: "Scope 2 Emissions (Metric Ton)",
    },
    {
      fieldName: "scope3EmissionsMetricTon",
      title: "Scope 3 Emissions (Metric Ton)",
    },
  ];

  const filterData = [
    {
      title: "Date",
      type: "date",
      key: "date",
      value: [],
    },
    {
      title: "Country",
      type: "option",
      key: "country",
      value: filters?.country || [],
    },
    {
      title: "Franchise Type",
      type: "option",
      key: "franchiseType",
      value: filters?.franchiseType || [],
    },
  ];

  const fetchData = async () => {
    try {
      const filter = { ...filterObj };

      // Apply date range filter (createdAt / date) similar to other Scope 3 modules
      if (filter.date) {
        filter.startDate = filter?.date?.start;
        filter.endDate = filter?.date?.end || new Date();
      }

      // Convert array filters to comma-separated strings for the backend
      // Remove empty arrays and undefined values
      Object.keys(filter).forEach((key) => {
        if (Array.isArray(filter[key])) {
          if (filter[key].length > 0) {
            filter[key] = filter[key].join(",");
          } else {
            delete filter[key];
          }
        } else if (filter[key] === undefined || filter[key] === null || filter[key] === "") {
          delete filter[key];
        }
      });

      const queryParams = new URLSearchParams({
        ...filter,
        page: currentPage,
        limit: 10,
        sortField: sortConfig.key,
        sortOrder: sortConfig.direction,
      }).toString();

      const res = await get(`/scope3/franchises/get?${queryParams}`);

      const arr =
        res?.data?.data?.map((item) => {
          const start = item?.reportingStart;
          const end = item?.reportingEnd;
          let reportingPeriod = "";

          if (start || end) {
            const startStr = start ? formatDate(start) : "";
            const endStr = end ? formatDate(end) : "";
            reportingPeriod = [startStr, endStr].filter(Boolean).join(" - ");
          }

          return {
            franchiseName: item?.franchiseName,
            country: item?.country,
            franchiseType: item?.franchiseType,
            reportingPeriod,
            action: "View",
            id: item?._id,
            ...item,
          };
        }) || [];

      setTableData(arr);
      setTotalPages(res?.data?.totalPages || 1);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [sortConfig, filterObj, currentPage]);

  const fetchCardData = async () => {
    try {
      const res = await get(`/scope3/franchises/cardData`);
      const data = res?.data?.data || {};
      setCardData({
        totalEntries: data?.totalEntries || 0,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const fetchFilters = async () => {
    try {
      const res = await get(`/scope3/franchises/filter`);
      const { country, franchiseType } = res?.data?.data || {};

      setFilters({
        country: country || [],
        franchiseType: franchiseType || [],
      });
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchCardData();
  }, []);

  useEffect(() => {
    fetchExcelData();
  }, []);

  useEffect(() => {
    fetchFilters();
  }, []);

  const uploadErrorClose = () => {
    setBulkUploadTable(true);
  };

  const handleCloseBulkUpload = () => {
    setBulkUploadModal(false);
    setBulkUploadTable(false);
    setMatchData([]);
    setFormDataBulkUpload([]);
    fetchData();
    fetchCardData();
  };

  const getUniqueValueCount = (values = []) => {
    return new Set(
      values
        .map((value) => (typeof value === "string" ? value.trim() : value))
        .filter((value) => value !== undefined && value !== null && value !== "")
    ).size;
  };

  const fetchMasterFranchises = async (franchiseId = "") => {
    try {
      const queryParams = new URLSearchParams({
        limit: "10",
        page: "1",
      });
      if (franchiseId?.trim()) {
        queryParams.set("franchiseId", franchiseId.trim());
      }
      const res = await get(`/master/franchises?${queryParams.toString()}`);
      setMasterFranchises(res?.data?.data || []);
    } catch (error) {
      console.error("Error fetching master franchises:", error);
      setMasterFranchises([]);
    }
  };

  useEffect(() => {
    const term = franchiseSearchTerm.trim();
    const shouldSearch = term.length === 0 || term.length >= 3;
    if (!shouldSearch) return;

    const timer = setTimeout(() => {
      fetchMasterFranchises(term);
    }, 3000);

    return () => clearTimeout(timer);
  }, [franchiseSearchTerm]);

  const bannerData = [
    {
      title: "Total Number of Franchises",
      description: `${cardData?.totalEntries ?? 0}`,
    },
    {
      title: "Total countries where franchises are present",
      description: `${getUniqueValueCount(filters?.country)}`,
    },
    {
      title: "Total franchises types",
      description: `${getUniqueValueCount(filters?.franchiseType)}`,
    }
  ];

  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleFilterChange = (newFilterObj) => {
    setFilterObj(newFilterObj);
    setCurrentPage(1);
  };

  const fetchExcelData = async () => {
    try {
      const res = await get(
        `/master/franchises?page=1&limit=1000&sortField=createdAt&sortOrder=desc`
      );
      const masters = Array.isArray(res?.data?.data) ? res.data.data : [];

      const template = masters.length
        ? masters.map((item) => ({
            "Franchise ID": item?.franchiseId || "",
            "Reporting Period Start Year (YYYY)": "",
            "Reporting Period End Year (YYYY)": "",
            "Scope 1 Emissions (Metric Ton)": "",
            "Scope 2 Emissions (Metric Ton)": "",
            "Scope 3 Emissions (Metric Ton)": "",
          }))
        : [
            {
              "Franchise ID": "",
              "Reporting Period Start Year (YYYY)": "",
              "Reporting Period End Year (YYYY)": "",
              "Scope 1 Emissions (Metric Ton)": "",
              "Scope 2 Emissions (Metric Ton)": "",
              "Scope 3 Emissions (Metric Ton)": "",
            },
          ];

      setExcelData(template);
    } catch (error) {
      console.error("Error generating franchises bulk upload template:", error);
      setExcelData([
        {
          "Franchise ID": "",
          "Reporting Period Start Year (YYYY)": "",
          "Reporting Period End Year (YYYY)": "",
          "Scope 1 Emissions (Metric Ton)": "",
          "Scope 2 Emissions (Metric Ton)": "",
          "Scope 3 Emissions (Metric Ton)": "",
        },
      ]);
    }
  };

  const openModal = (type, dataForEdit) => {
    if (type === "add") {
      setIsModalOpen(true);
      setEditModal(false);
      setEditData({});
    } else if (type === "edit") {
      setIsModalOpen(true);
      setEditData(dataForEdit);
      setEditModal(true);
      setViewModal(false);
    }
  };

  const handleEdit = (data) => {
    openModal("edit", data);
  };

  const openViewModal = (data) => {
    setEditData(data);
    setViewModal(true);
  };

  const closeModal = (type) => {
    if (type === "add") {
      setIsModalOpen(false);
    } else if (type === "edit") {
      setIsModalOpen(false);
      setEditModal(false);
      setEditData({});
    }
  };

  const handleSubmit = async (data, isEditing) => {
    try {
      const url = isEditing
        ? `/scope3/franchises/update?Id=${editData.id || editData._id}`
        : `/scope3/franchises/add`;

      const apiCall = isEditing ? put : post;

      const payload = {
        ...data,
        reportingStart: data.reportingPeriodStart,
        reportingEnd: data.reportingPeriodEnd,
      };
      delete payload.reportingPeriodStart;
      delete payload.reportingPeriodEnd;

      const res = await apiCall(url, payload);

      if (res) {
        setsnackOpen({
          open: true,
          message: res?.data?.message,
          severity: "success",
        });

        setIsModalOpen(false);

        if (isEditing) {
          setEditModal(false);
          setEditData({});
        }

        fetchCardData();
        fetchData();
      }
    } catch (error) {
      setsnackOpen({
        open: true,
        message: error?.response?.data?.message || "Something went wrong",
        severity: "error",
      });

      console.error("Error in handleSubmit (Franchises):", error);
    }
  };

  const handleDeleteOpen = (row) => {
    setDeletedItemId(row);
    setDeleteDialog(true);
  };



  return (
    <>
      <Cards data={bannerData} title={"Franchises"} />

      <ActionButtons
        onFilterClick={() => setIsFilterModalOpen(true)}
        onAddClick={() => openModal("add")}
        filterObj={filterObj}
        onUploadClick={() => setBulkUploadModal(true)}
      />

      <ViewTable
        title={"Franchises"}
        headers={headers}
        data={tableData ?? []}
        onSort={handleSort}
        sortConfig={sortConfig}
        page={currentPage}
        count={10}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        onView={(row) => {
          openViewModal(row);
        }}
        onDelete={(row) => handleDeleteOpen(row)}
      />

      <Snackbar
        open={snackOpen.open}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setsnackOpen({ ...snackOpen, open: false })}
      >
        <Alert
          onClose={() => setsnackOpen({ ...snackOpen, open: false })}
          severity={snackOpen.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackOpen.message}
        </Alert>
      </Snackbar>

      <Form
        isOpen={isModalOpen}
        onClose={() => closeModal(editModal ? "edit" : "add")}
        onSubmit={handleSubmit}
        header={editModal ? "Edit Franchise" : "Add Franchise"}
        initialData={editData}
        isEditing={editModal}
        franchiseOptions={masterFranchises}
        onSearchFranchises={setFranchiseSearchTerm}
      />

      <ViewData
        id={editData ? editData.id || editData._id : ""}
        element={editData}
        title={"Franchises"}
        viewData={viewModal}
        setViewData={setViewModal}
        setData={setEditData}
        inputData={inputData}
      />

      {deleteDialog && (
        <DeleteAlertDialog
          {...{
            deleteDialog,
            setDeleteDialog,
            method: "delete",
            id: deletedItemId?.id || deletedItemId?._id,
            url: `${api}/scope3/franchises/delete`,
            setsnackOpen,
            fetchData,
            fetchCardData,
          }}
        />
      )}

      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        filters={filterData}
        onFilterChange={handleFilterChange}
        title="Filter"
      />

      {bulkUploadModal && (
        <BulkUploadModal
          onCloseBulkUpload={handleCloseBulkUpload}
          setMatchData={setMatchData}
          setFormDataBulkUpload={setFormDataBulkUpload}
          matchedKey="franchiseStatusList"
          errorClose={uploadErrorClose}
          setBulkUploadModal={setBulkUploadModal}
          isBulkUploadModal={bulkUploadModal}
          excelData={excelData}
          fileName={"franchises"}
          setsnackOpen={setsnackOpen}
          url={"/scope3/franchises/bulkupload"}
        />
      )}

      {bulkUploadTable && (
        <BulkUploadReview
          bulkUploadTable={bulkUploadTable}
          matchData={matchData}
          setMatchData={setMatchData}
          formDataBulkUpload={formDataBulkUpload}
          setFormDataBulkUpload={setFormDataBulkUpload}
          setsnackOpen={setsnackOpen}
          bulkUploadUrl={"/scope3/franchises/jsonupload"}
          onClose={() => setBulkUploadTable(false)}
          onConfirm={() => {
            setBulkUploadTable(false);
            fetchData();
            fetchCardData();
          }}
        />
      )}
    </>
  );
};

export default Franchises;

